import * as React from 'react';

import { getEmojiFlag, TCountryCode } from 'countries-list';
import {
  PhoneNumber,
  PhoneNumberFormat,
  PhoneNumberUtil,
} from 'google-libphonenumber';
import { LucideCopy } from 'lucide-react';

import { useToast } from '../../../hooks/useToast';
import { Maybe } from '../../../lib';

const phoneUtil = new PhoneNumberUtil();

export type LocalizedPhoneProps = {
  children: Maybe<string>;
};

export const LocalizedPhone = ({
  children: phoneNumber,
}: LocalizedPhoneProps) => {
  const toast = useToast();
  const parsedPhoneNumber = React.useMemo(() => {
    try {
      const parsedPhone = phoneNumber
        ? phoneUtil.parseAndKeepRawInput(phoneNumber)
        : undefined;

      return parsedPhone;
    } catch (_error) {
      // If we can't parse the phone number we will simply render the raw input
      return undefined;
    }
  }, [phoneNumber]);

  const flag = React.useMemo(() => {
    if (!parsedPhoneNumber) return undefined;

    const regionCode = phoneUtil.getRegionCodeForNumber(parsedPhoneNumber);
    if (!regionCode) return undefined;

    return getEmojiFlag(regionCode as TCountryCode);
  }, [parsedPhoneNumber]);

  const formattedPhoneNumber = React.useMemo(() => {
    if (!parsedPhoneNumber) {
      return '';
    }

    if (
      parsedPhoneNumber.getCountryCodeSource() ===
      PhoneNumber.CountryCodeSource.FROM_DEFAULT_COUNTRY
    ) {
      return phoneUtil.format(
        parsedPhoneNumber,
        PhoneNumberFormat.INTERNATIONAL,
      );
    } else {
      return phoneUtil.format(parsedPhoneNumber, PhoneNumberFormat.NATIONAL);
    }
  }, [parsedPhoneNumber]);

  if (!phoneNumber) {
    return <>–</>;
  }

  return (
    <div className="@LocalizedPhone flex items-center gap-2">
      {flag && flag + ' '}
      {formattedPhoneNumber}
      <LucideCopy
        size={12}
        className="cursor-pointer text-neutral/40 hover:text-primary"
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          navigator.clipboard.writeText(phoneNumber);
          toast.success('Phone number copied to clipboard');
        }}
      />
    </div>
  );
};
